import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Badge } from '../../components/ui/Kit';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  DASHBOARD_PERIOD_IDS,
  DASHBOARD_PERIOD_OPTIONS,
  getDashboardPeriodLabel,
  getDateRange,
} from '../../lib/getDateRange';
import { format, parseISO } from 'date-fns';
import { CHANNEL_COLORS, CHANNEL_LABELS } from '../../lib/leadMeta';
import TeamScopeFilter from '../Pipeline/TeamScopeFilter';
import {
  isPipelineLeadership,
  defaultTeamScope,
} from '../Pipeline/pipelineUtils';
import { useVisiblePolling } from '../../lib/useVisiblePolling';

const APPT_TYPE_LABELS = {
  intro_call: 'Intro Call',
  presentation: 'Presentation',
  follow_up: 'Follow-Up',
  closing_call: 'Closing Call',
};

function DashAppointmentTypeBadge({ type }) {
  const label = APPT_TYPE_LABELS[type] || String(type || '').replace(/_/g, ' ');
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-[100px] text-[10px] font-semibold uppercase tracking-wide bg-skyTint text-navy">
      {label}
    </span>
  );
}

function DashAppointmentStatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const map = {
    scheduled: { variant: 'primary', label: 'Scheduled' },
    confirmed: { variant: 'success', label: 'Confirmed' },
    completed: { variant: 'default', label: 'Completed' },
    cancelled: { variant: 'danger', label: 'Cancelled' },
    no_show: { variant: 'warning', label: 'No Show' },
  };
  const x = map[s] || { variant: 'default', label: status || '—' };
  return <Badge variant={x.variant}>{x.label}</Badge>;
}

const {
  FiUsers,
  FiPhoneCall,
  FiCalendar,
  FiRefreshCw,
  FiBarChart2,
  FiUserPlus,
  FiZap,
} = FiIcons;

const EMPTY_AI_CALLER_STATS = {
  totalCalls: 0,
  appointmentsBooked: 0,
  bookingRate: 0,
  outcomes: { qualified: 0, not_interested: 0, no_answer: 0, voicemail: 0 },
};

const AI_OUTCOME_BAR = [
  { key: 'qualified', label: 'Qualified', color: '#00AAFF' },
  { key: 'not_interested', label: 'Not interested', color: '#9CA3AF' },
  { key: 'no_answer', label: 'No answer', color: '#6B7280' },
  { key: 'voicemail', label: 'Voicemail', color: '#A78BFA' },
];

function AiCallerPerformanceWidget({ stats, periodId }) {
  const outcomes = stats?.outcomes ?? EMPTY_AI_CALLER_STATS.outcomes;
  const totalOutcomeCount = AI_OUTCOME_BAR.reduce((sum, row) => sum + (outcomes[row.key] ?? 0), 0);
  const hasCalls = (stats?.totalCalls ?? 0) > 0;

  return (
    <Card className="p-6">
      <WidgetTitle periodId={periodId}>
        <span className="inline-flex items-center gap-2">
          <SafeIcon icon={FiZap} className="h-5 w-5 text-violet-500" />
          AI Caller Performance
        </span>
      </WidgetTitle>

      {!hasCalls ? (
        <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
          <SafeIcon icon={FiZap} className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No AI calls in this period yet</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total AI calls</p>
              <p className="text-2xl font-heading font-bold text-gray-900 mt-1 tabular-nums">{stats.totalCalls}</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-green-50/80 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Appts booked by AI</p>
              <p className="text-2xl font-heading font-bold text-success mt-1 tabular-nums">{stats.appointmentsBooked}</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-violet-50/80 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">AI booking rate</p>
              <p className="text-2xl font-heading font-bold text-violet-700 mt-1 tabular-nums">{stats.bookingRate}%</p>
            </div>
          </div>

          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Outcome breakdown</p>
          {totalOutcomeCount === 0 ? (
            <p className="text-sm text-gray-400">No completed outcomes yet in this period.</p>
          ) : (
            <>
              <div className="flex h-3 w-full rounded-full overflow-hidden bg-gray-100">
                {AI_OUTCOME_BAR.map((row) => {
                  const count = outcomes[row.key] ?? 0;
                  if (!count) return null;
                  const pct = (count / totalOutcomeCount) * 100;
                  return (
                    <div
                      key={row.key}
                      className="h-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: row.color }}
                      title={`${row.label}: ${count}`}
                    />
                  );
                })}
              </div>
              <ul className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-600">
                {AI_OUTCOME_BAR.map((row) => (
                  <li key={row.key} className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: row.color }}
                    />
                    {row.label}: <span className="font-semibold text-gray-900">{outcomes[row.key] ?? 0}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Card>
  );
}

const GOAL_RING_CIRCUMFERENCE = 2 * Math.PI * 70;

const emptyCallOutcomes = () => ({
  answered: 0,
  voicemail: 0,
  no_answer: 0,
  busy: 0,
  booked_meeting: 0,
  closed_business: 0,
});

const CHANNEL_ACTIVITY_METRICS = [
  { key: 'leadsAdded', label: 'Leads added' },
  { key: 'callsMade', label: 'Calls made' },
  { key: 'appointmentsSet', label: 'Appts set' },
  { key: 'appsSubmitted', label: 'Apps submitted' },
];

function ActivityByChannelWidget({ activity, periodId }) {
  const channels = ['LD', 'IM', 'AM'];

  return (
    <Card className="p-6">
      <WidgetTitle periodId={periodId}>Activity by Channel</WidgetTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {channels.map((ch) => {
          const stats = activity?.[ch] ?? {};
          const hasActivity = CHANNEL_ACTIVITY_METRICS.some((m) => (stats[m.key] ?? 0) > 0);
          return (
            <div
              key={ch}
              className="rounded-xl border border-gray-200 overflow-hidden"
              style={{ borderTopWidth: 4, borderTopColor: CHANNEL_COLORS[ch] }}
            >
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <p className="text-sm font-heading font-bold text-gray-900">{CHANNEL_LABELS[ch]}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{ch}</p>
              </div>
              <div className="p-4">
                {!hasActivity ? (
                  <p className="text-sm text-gray-400 text-center py-4">No activity yet</p>
                ) : (
                  <ul className="space-y-2">
                    {CHANNEL_ACTIVITY_METRICS.map((m) => (
                      <li key={m.key} className="flex justify-between text-sm">
                        <span className="text-gray-500">{m.label}</span>
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {stats[m.key] ?? 0}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const EMPTY_TEAM_PIPELINE = {
  inviteLinkClicks: 0,
  eventRegistrations: 0,
  callsMade: 0,
  launchesByStatus: { not_started: 0, scheduled: 0, completed: 0 },
  top25Total: 0,
  top25Converted: 0,
};

function TeamPipelineOverviewWidget({ user, periodId }) {
  const [scope, setScope] = useState(() => defaultTeamScope(user?.role));
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(EMPTY_TEAM_PIPELINE);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user?.agency_id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const range = getDateRange(periodId);
        let associateIds = null;
        if (scope === 'my_team') {
          const team = await api.users.getMyTeam();
          associateIds = (team || []).map((u) => u.id);
        }
        const overview = await api.dashboard.getTeamPipelineOverview(user.agency_id, {
          ...range,
          associateIds,
        });
        if (!cancelled) setStats(overview || EMPTY_TEAM_PIPELINE);
      } catch (err) {
        console.error(err);
        if (!cancelled) setStats(EMPTY_TEAM_PIPELINE);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.agency_id, periodId, scope]);

  const launchTotal =
    (stats.launchesByStatus?.not_started || 0)
    + (stats.launchesByStatus?.scheduled || 0)
    + (stats.launchesByStatus?.completed || 0);

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <WidgetTitle periodId={periodId}>Team Pipeline Overview</WidgetTitle>
        <TeamScopeFilter value={scope} onChange={setScope} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 gap-3">
          <Spinner className="text-primary" />
          <p className="text-sm text-gray-500">Loading team metrics…</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Invite clicks</p>
              <p className="text-2xl font-heading font-bold text-gray-900 mt-1 tabular-nums">{stats.inviteLinkClicks}</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Event regs</p>
              <p className="text-2xl font-heading font-bold text-gray-900 mt-1 tabular-nums">{stats.eventRegistrations}</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Calls made</p>
              <p className="text-2xl font-heading font-bold text-gray-900 mt-1 tabular-nums">{stats.callsMade}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">Same period as dashboard</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Top 25</p>
              <p className="text-2xl font-heading font-bold text-gray-900 mt-1 tabular-nums">
                {stats.top25Converted}
                <span className="text-base font-semibold text-gray-500">/{stats.top25Total}</span>
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5">Converted / total</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Active Business Launches ({launchTotal})
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'not_started', label: 'Not started' },
                { key: 'scheduled', label: 'Scheduled' },
                { key: 'completed', label: 'Completed' },
              ].map((row) => (
                <div key={row.key} className="rounded-lg border border-gray-100 px-3 py-2.5 text-center">
                  <p className="text-[11px] text-gray-500">{row.label}</p>
                  <p className="text-xl font-heading font-bold text-navy tabular-nums">
                    {stats.launchesByStatus?.[row.key] ?? 0}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function WidgetTitle({ children, periodId }) {
  const showPeriod = periodId !== DASHBOARD_PERIOD_IDS.TODAY;
  const label = getDashboardPeriodLabel(periodId);
  return (
    <h3 className="text-lg font-heading font-bold text-gray-900 mb-4 flex flex-wrap items-baseline gap-x-1 gap-y-1">
      <span>{children}</span>
      {showPeriod ? (
        <span className="text-sm font-normal text-gray-400">
          {' '}
          · {label}
        </span>
      ) : null}
    </h3>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState(DASHBOARD_PERIOD_IDS.TODAY);
  const [stats, setStats] = useState({
    newLeadsCount: 0,
    callAttempts: 0,
    leadsCalledDistinct: 0,
    appointmentsSet: 0,
    callOutcomes: emptyCallOutcomes(),
    dailyCallGoal: 100,
    callAttemptsToday: 0,
    salesPipeline: [],
    recruitingByStage: [],
  });
  const [channelActivity, setChannelActivity] = useState({
    LD: { leadsAdded: 0, callsMade: 0, appointmentsSet: 0, appsSubmitted: 0 },
    IM: { leadsAdded: 0, callsMade: 0, appointmentsSet: 0, appsSubmitted: 0 },
    AM: { leadsAdded: 0, callsMade: 0, appointmentsSet: 0, appsSubmitted: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [todayAppointmentsLoading, setTodayAppointmentsLoading] = useState(false);
  const [aiCallerStats, setAiCallerStats] = useState(EMPTY_AI_CALLER_STATS);

  const loadDashboard = useCallback(
    async (isRefresh = false) => {
      const agencyId = user?.agency_id;
      if (!agencyId) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const range = getDateRange(period);
        const [snapshot, calling, pipeline, dailyGoal, byChannel, aiCaller] = await Promise.all([
          api.dashboard.getSnapshot(agencyId, range),
          api.dashboard.getCallingActivity(agencyId, range),
          api.dashboard.getPipelineSummary(agencyId),
          api.dashboard.getDailyGoalProgress(agencyId),
          api.dashboard.getChannelActivity(agencyId, range),
          api.dashboard.getAiCallerPerformance(agencyId, range),
        ]);
        setChannelActivity(byChannel);
        setAiCallerStats(aiCaller ?? EMPTY_AI_CALLER_STATS);
        setStats({
          newLeadsCount: snapshot.newLeadsCount ?? 0,
          callAttempts: snapshot.callAttempts ?? 0,
          leadsCalledDistinct: snapshot.leadsCalledDistinct ?? 0,
          appointmentsSet: snapshot.appointmentsSet ?? 0,
          callOutcomes: { ...emptyCallOutcomes(), ...calling },
          dailyCallGoal: dailyGoal.dailyCallGoal ?? snapshot.dailyCallGoal ?? 100,
          callAttemptsToday: dailyGoal.callAttemptsToday ?? 0,
          salesPipeline: pipeline.salesPipeline ?? [],
          recruitingByStage: pipeline.recruitingByStage ?? [],
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user?.agency_id, period],
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const loadTodayAppointments = useCallback(async () => {
    const agencyId = user?.agency_id;
    if (!agencyId) {
      setTodayAppointments([]);
      return;
    }
    setTodayAppointmentsLoading(true);
    try {
      const rows = await api.appointments.getToday(agencyId);
      setTodayAppointments(rows || []);
    } catch (err) {
      console.error(err);
      setTodayAppointments([]);
    } finally {
      setTodayAppointmentsLoading(false);
    }
  }, [user?.agency_id]);

  useEffect(() => {
    loadTodayAppointments();
  }, [loadTodayAppointments]);

  const refreshDailyGoal = useCallback(async () => {
    const agencyId = user?.agency_id;
    if (!agencyId) return;
    try {
      const dailyGoal = await api.dashboard.getDailyGoalProgress(agencyId);
      setStats((prev) => ({
        ...prev,
        dailyCallGoal: dailyGoal.dailyCallGoal ?? prev.dailyCallGoal,
        callAttemptsToday: dailyGoal.callAttemptsToday ?? 0,
      }));
    } catch (err) {
      console.error(err);
    }
  }, [user?.agency_id]);

  useVisiblePolling(refreshDailyGoal, 60_000, Boolean(user?.agency_id));

  const newLeadsTitle =
    period === DASHBOARD_PERIOD_IDS.TODAY
      ? 'New Today'
      : `New ${getDashboardPeriodLabel(period)}`;

  const goal = Math.max(1, stats.dailyCallGoal || 100);
  const goalProgress = Math.min(stats.callAttemptsToday / goal, 1);
  const goalDash = goalProgress * GOAL_RING_CIRCUMFERENCE;

  const pipelineHasCounts = stats.salesPipeline.some((s) => s.count > 0);
  const recruitingHasCounts = stats.recruitingByStage.some((s) => s.count > 0);

  const hasAnyData =
    stats.newLeadsCount > 0 ||
    stats.callAttempts > 0 ||
    stats.leadsCalledDistinct > 0 ||
    stats.appointmentsSet > 0 ||
    pipelineHasCounts ||
    recruitingHasCounts;

  const outcomeItems = [
    { label: 'Answered', value: stats.callOutcomes.answered, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Voicemail', value: stats.callOutcomes.voicemail, color: 'text-warning', bg: 'bg-warningBg' },
    { label: 'No answer', value: stats.callOutcomes.no_answer, color: 'text-slate-600', bg: 'bg-slate-50' },
    { label: 'Busy', value: stats.callOutcomes.busy, color: 'text-rose-600', bg: 'bg-rose-50' },
    { label: 'Booked', value: stats.callOutcomes.booked_meeting, color: 'text-[#00AAFF]', bg: 'bg-[#00AAFF]/10' },
    { label: 'Closed', value: stats.callOutcomes.closed_business, color: 'text-[#22c55e]', bg: 'bg-[#22c55e]/10' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-gray-500 text-sm">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">
            Welcome back, {user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Here&apos;s what&apos;s happening in your agency.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <label className="sr-only" htmlFor="dashboard-period">
            Time period
          </label>
          <select
            id="dashboard-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-8 min-w-[9.5rem] rounded-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {DASHBOARD_PERIOD_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadDashboard(true);
              loadTodayAppointments();
            }}
            disabled={refreshing}
          >
            {refreshing ? (
              <Spinner className="mr-2" />
            ) : (
              <SafeIcon icon={FiRefreshCw} className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          className="p-6 flex items-center gap-4 cursor-pointer hover:border-navy/20 transition-colors"
          onClick={() => navigate('/leads')}
        >
          <div className="p-3 rounded-lg bg-blue-50">
            <SafeIcon icon={FiUsers} className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">{newLeadsTitle}</p>
            <p className="text-2xl font-heading font-bold text-gray-900">{stats.newLeadsCount}</p>
          </div>
        </Card>

        <Card className="p-6 flex items-center gap-4">
          <div className="p-3 rounded-lg bg-purple-50">
            <SafeIcon icon={FiPhoneCall} className="h-6 w-6 text-purple-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-500">Call Activity</p>
            <p className="mt-1">
              <span className="text-3xl font-heading font-bold text-gray-900 tabular-nums">{stats.callAttempts}</span>
              <span className="text-lg font-bold text-gray-900 ml-1.5">Call Attempts</span>
            </p>
            <p className="text-sm text-gray-500 mt-1 tabular-nums">
              {stats.leadsCalledDistinct} Leads Called
            </p>
          </div>
        </Card>

        <Card className="p-6 flex items-center gap-4">
          <div className="p-3 rounded-lg bg-orange-50">
            <SafeIcon icon={FiCalendar} className="h-6 w-6 text-orange-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Appts Set</p>
            <p className="text-2xl font-heading font-bold text-gray-900">{stats.appointmentsSet}</p>
          </div>
        </Card>
      </div>

      {isPipelineLeadership(user?.role) && (
        <TeamPipelineOverviewWidget user={user} periodId={period} />
      )}

      <ActivityByChannelWidget activity={channelActivity} periodId={period} />

      <AiCallerPerformanceWidget stats={aiCallerStats} periodId={period} />

      <Card className="p-6">
        <WidgetTitle periodId={period}>Calling Activity</WidgetTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {outcomeItems.map((row) => (
            <div
              key={row.label}
              className={`rounded-lg px-4 py-3 ${row.bg} border border-gray-100`}
            >
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{row.label}</p>
              <p className={`text-2xl font-heading font-bold mt-1 ${row.color}`}>{row.value}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <WidgetTitle>Sales Pipeline Overview</WidgetTitle>
          <p className="text-xs text-gray-500 -mt-2 mb-4">Current stage distribution</p>
          {!pipelineHasCounts && !hasAnyData ? (
            <EmptyState
              icon={FiBarChart2}
              title="No pipeline data yet"
              description="Add leads and move them through your pipeline to see activity here."
              actionLabel="Add First Lead"
              onAction={() => navigate('/leads/new')}
            />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.salesPipeline}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis type="number" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip cursor={{ fill: '#f3f4f6' }} />
                  <Bar
                    dataKey="count"
                    fill="#00AAFF"
                    radius={[0, 4, 4, 0]}
                    barSize={24}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-6 flex flex-col">
          <h3 className="text-lg font-heading font-bold text-gray-900 mb-4">Daily Call Goal</h3>
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative w-40 h-40 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  fill="none"
                  stroke="#f3f4f6"
                  strokeWidth="12"
                />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  fill="none"
                  stroke="#00AAFF"
                  strokeWidth="12"
                  strokeDasharray={`${goalDash} ${GOAL_RING_CIRCUMFERENCE}`}
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute text-center">
                <span className="text-3xl font-heading font-bold text-gray-900">
                  {stats.callAttemptsToday}
                </span>
                <span className="text-sm text-gray-500 block">/ {goal}</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Today</p>
            <p className="mt-4 text-sm text-center text-gray-500">
              {stats.callAttemptsToday >= goal
                ? 'Goal reached! Great job.'
                : stats.callAttemptsToday === 0
                  ? `Your team’s daily target is ${goal} calls.`
                  : `${goal - stats.callAttemptsToday} more calls to reach your goal.`}
            </p>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-heading font-bold text-gray-900 mb-1 flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="h-5 w-5 text-[#00AAFF]" />
          Today&apos;s Appointments
        </h3>
        <p className="text-xs text-gray-500 mb-4">Always today — not filtered by the dashboard period.</p>
        {todayAppointmentsLoading ? (
          <div className="flex items-center gap-2 py-8 text-gray-500 text-sm">
            <Spinner className="text-primary" />
            Loading…
          </div>
        ) : todayAppointments.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No appointments scheduled for today.</p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            {todayAppointments.map((row) => {
              const leadName = row.lead
                ? `${row.lead.first_name || ''} ${row.lead.last_name || ''}`.trim() || 'Lead'
                : 'Lead';
              const agentName = row.assignee?.full_name || row.assignee?.email || '—';
              const t = row.scheduled_at ? format(parseISO(row.scheduled_at), 'h:mm a') : '—';
              return (
                <li key={row.id} className="flex flex-wrap items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50/80">
                  <span className="text-sm font-semibold text-gray-900 tabular-nums w-20 shrink-0">{t}</span>
                  <button
                    type="button"
                    onClick={() => row.lead_id && navigate(`/leads/${row.lead_id}`)}
                    className="text-sm font-medium text-[#00AAFF] hover:underline text-left min-w-0 flex-1"
                    disabled={!row.lead_id}
                  >
                    {leadName}
                  </button>
                  <span className="text-xs text-gray-600 shrink-0 max-w-[140px] truncate" title={agentName}>
                    {agentName}
                  </span>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <DashAppointmentTypeBadge type={row.appointment_type} />
                    <DashAppointmentStatusBadge status={row.status} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card className="p-6">
        <WidgetTitle>
          <span className="inline-flex items-center gap-2">
            <SafeIcon icon={FiUserPlus} className="h-5 w-5 text-gray-600" />
            Recruiting pipeline
          </span>
        </WidgetTitle>
        <p className="text-xs text-gray-500 -mt-2 mb-4">Current stage distribution</p>
        {!recruitingHasCounts ? (
          <p className="text-sm text-gray-500">No recruits in the pipeline yet.</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stats.recruitingByStage}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={100}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip cursor={{ fill: '#f3f4f6' }} />
                <Bar
                  dataKey="count"
                  fill="#10B981"
                  radius={[0, 4, 4, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}
